import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import api from "../lib/api";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryModal } from "../components/EnquiryModal";
import { useTitle } from "../hooks/useTitle";

export default function Testimonials() {
  useTitle("Testimonials");
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="testimonials-page">
      <section className="bg-[#0A192F] relative overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 hero-grid" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow light>Testimonials</Eyebrow>
          <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 max-w-2xl leading-[1.1]">
            Real students. Real words. Nothing fabricated.
          </h1>
          <p className="mt-5 text-slate-300 text-base max-w-xl leading-relaxed">
            We publish only genuine, verifiable feedback from learners who trained with us.
          </p>
        </div>
      </section>
      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {items.length === 0 ? (
          <Reveal>
            <div className="bg-white rounded-2xl border border-dashed border-[#C59B27]/60 p-12 text-center max-w-2xl mx-auto" data-testid="testimonials-page-empty">
              <Quote size={30} className="text-[#C59B27] mx-auto" />
              <p className="mt-5 text-[#486581] text-sm leading-relaxed">
                Our first graduates' stories are being written right now. Train with us, build real skills — and your
                testimonial could be the first one published here.
              </p>
              <EnquiryModal
                source="testimonials-page"
                trigger={
                  <button data-testid="testimonials-enquire-btn" className="mt-7 px-7 py-3.5 rounded-full bg-[#0A192F] text-white font-display font-bold text-sm hover:bg-[#0F5132] transition-colors">
                    Start Learning
                  </button>
                }
              />
            </div>
          </Reveal>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div className="h-full bg-white rounded-2xl border border-slate-200 p-7" data-testid={`testimonial-page-card-${i}`}>
                  <Quote size={22} className="text-[#C59B27]" />
                  <p className="mt-4 text-sm text-[#334E68] leading-relaxed italic">"{t.quote}"</p>
                  <div className="mt-6 flex items-center gap-3">
                    {t.photo_url ? (
                      <img src={t.photo_url} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-[#0A192F] flex items-center justify-center text-[#C59B27] font-display font-bold">
                        {t.name?.[0]}
                      </div>
                    )}
                    <div>
                      <p className="font-display font-bold text-sm text-[#0A192F]">{t.name}</p>
                      <p className="text-xs text-[#627D98]">{t.role}{t.course ? ` · ${t.course}` : ""}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
